import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, Download } from 'lucide-react';
import { SkeletonTableRows } from './ReportSkeleton';
import { VarianceChip } from './VarianceChip';
import { CardLegend } from './ReportCard';
import { formatValue } from './formatters';
import './ReportsNative.css';
import { ColumnPickerControl, applyColumnPrefs } from './ColumnPicker';
import { useFullscreenCard, FullscreenButton, FullscreenBackdrop } from './Fullscreen';

/**
 * ReportMatrix — hierarchical collapsible matrix (per financial-pnl.html
 * Income Statement & operations-labor.html Labor Metrics / Labor Hours).
 *
 * Renders its own card with a Geist toolbar title, sticky header + sticky
 * first column, column groups with hairline separators, mono numerals,
 * variance chips, muted budget columns, emphasized total column group,
 * and a sticky onyx-900 footer totals row. Nulls render "—".
 *
 * Props:
 *   title, pill, tools         toolbar: serif h2, orange pill note, extra nodes
 *   rowHeaderLabel             top-left header ('Location / Account')
 *   columns                    [{ key, label, group?, align?, format?,
 *                                 variant?: 'value'|'budget'|'variance'|'total',
 *                                 invertVariance?, width?, render?(value,row,col),
 *                                 side?: 'left' }]
 *                              — columns flagged side:'left' render BEFORE the
 *                                row-header column, which then sits between the
 *                                two column sets (the printed "Period | Description
 *                                | Year to Date" statement) and pins to whichever
 *                                edge of the pane it would otherwise scroll past
 *   totalGroup                 group name to emphasize as the Total column
 *                              group (warm tint + orange group header)
 *   rows                       [{ id, parentId?, level?, label, cells: {},
 *                                 variant?: 'subtotal'|'emphasis' }]
 *                              — must be in display (depth-first) order,
 *                                parents before children
 *   footer                     { label, cells: {} } sticky totals row
 *   currency                   ISO code for currency formats
 *   expanded                   controlled expand state: array or Set of row ids
 *   onExpandedChange           (nextIdsArray) => void
 *   defaultExpanded            'roots' (default) | 'all' | 'none' | array of ids
 *   expandControls             show expand/collapse icon buttons — each click
 *                              opens/closes ONE hierarchy level (default true)
 *   onDownloadCsv              show CSV button; called with no args
 *   downloadOptions            [{ key, label, hint?, onClick }] — the download
 *                              icon opens a menu of export choices instead of
 *                              downloading directly (wins over onDownloadCsv)
 *   onEndReached               endless scroll: called when the viewport nears
 *                              the bottom (caller appends the next page)
 *   legend                     CardLegend items (strings or {label,swatch,border})
 *   maxHeight                  scroll viewport height. Default 'fit' measures
 *                              the pane's viewport position so its bottom edge
 *                              (and the sticky totals footer) stays on screen
 *                              while rows scroll within — tall displays show
 *                              more rows, the footer never slips past the
 *                              fold. Pass a number to pin it (drill modals,
 *                              side-by-side panes); 410px floor either way
 *   loading, emptyText
 */
/**
 * Download-icon dropdown for tables with more than one export shape (e.g. the
 * P&L's current-view vs raw-data exports). Portals to <body> at fixed position
 * like the column picker — .rn-card clips overflow, an in-card menu would too.
 */
function ExportMenuControl({ options }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const compute = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) setPos({ top: rect.bottom + 6, right: Math.max(12, window.innerWidth - rect.right) });
    };
    compute();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  return (
    <span className="rn-colpicker" ref={wrapRef}>
      <button
        type="button"
        className={`rn-icon-btn${open ? ' rn-icon-btn-active' : ''}`}
        title="Export"
        onClick={() => setOpen((v) => !v)}
      >
        <Download />
      </button>
      {open && pos && createPortal(
        <div
          className="rn-export-menu"
          role="menu"
          ref={popRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
        >
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className="rn-export-menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                opt.onClick();
              }}
            >
              <span>{opt.label}</span>
              {opt.hint && <span className="rn-export-menu-hint">{opt.hint}</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </span>
  );
}

export function ReportMatrix({
  title,
  pill,
  tools,
  rowHeaderLabel = '',
  columns: columnsProp = [],
  columnPrefs,
  totalGroup,
  rows = [],
  footer,
  currency,
  expanded,
  onExpandedChange,
  defaultExpanded = 'roots',
  expandControls = true,
  fullscreenControl = true,
  onDownloadCsv,
  downloadOptions,
  // Extra checkbox groups inside the Columns picker (see ColumnPickerControl)
  columnPickerSections,
  legend,
  onRowClick,
  selectedRowId,
  onEndReached,
  // ≈10 data rows in view (rows ~31px + grouped header + sticky footer);
  // fullscreen unclamps and fills the screen instead
  maxHeight = 'fit',
  loading = false,
  emptyText = 'No data for the selected filters.',
  className,
  // Narrative-mode hooks: rows may carry `severity` ('red'|'yellow'|'green')
  // for a flag rail, or `fullRender` + `anchorId` for a full-width detail row
  // glued under its anchor. renderRowHeaderExtra(row) appends a node to the
  // row header cell (e.g. a notes affordance).
  renderRowHeaderExtra,
}) {
  const prefColumns = applyColumnPrefs(columnsProp, columnPrefs);
  // Split layout: side:'left' columns lead, the row header sits in the
  // middle, the rest follow. `columns` keeps the rendered order so group
  // separators / sort / CSV helpers see one contiguous list.
  const leftColumns = useMemo(() => prefColumns.filter((c) => c.side === 'left'), [prefColumns]);
  const centered = leftColumns.length > 0;
  const rightColumns = useMemo(
    () => (centered ? prefColumns.filter((c) => c.side !== 'left') : prefColumns),
    [prefColumns, centered]
  );
  const columns = useMemo(() => (centered ? [...leftColumns, ...rightColumns] : prefColumns), [centered, leftColumns, rightColumns, prefColumns]);
  const currencyOpts = currency ? { currency } : undefined;

  /* ---------- column sort (hierarchy-aware: siblings within each parent) ---------- */
  // The two header rows are both sticky; the column row must pin exactly
  // below the group row. Measuring beats a hardcoded offset — the group row's
  // height moves with font, zoom and border rounding (it renders 29px, not the
  // 28px you'd compute), and a stale constant shows up as a 1px seam or a
  // hairline of data bleeding between the rows while scrolling.
  const matrixRef = useRef(null);
  useEffect(() => {
    const table = matrixRef.current;
    if (!table) return undefined;
    const groupRow = table.querySelector('thead tr.rn-mx-groups');
    if (!groupRow) {
      table.style.removeProperty('--rn-mx-group-h');
      return undefined;
    }
    const apply = () => {
      const h = groupRow.getBoundingClientRect().height;
      if (h > 0) table.style.setProperty('--rn-mx-group-h', `${Math.round(h)}px`);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(groupRow);
    return () => ro.disconnect();
  });

  const [sort, setSort] = useState(null); // { field, dir } | null
  const toggleSort = useCallback(
    (field) =>
      setSort((cur) => {
        if (!cur || cur.field !== field) return { field, dir: 'desc' };
        if (cur.dir === 'desc') return { field, dir: 'asc' };
        return null; // third click clears → original order
      }),
    []
  );

  /* ---------- hierarchy bookkeeping ---------- */
  const { parentIds, levels } = useMemo(() => {
    const parents = new Set();
    const byId = new Map();
    rows.forEach((r) => byId.set(r.id, r));
    rows.forEach((r) => {
      if (r.parentId !== undefined && r.parentId !== null) parents.add(r.parentId);
    });
    // derive level from parent chain when not provided
    const lvls = new Map();
    const levelOf = (r) => {
      if (lvls.has(r.id)) return lvls.get(r.id);
      let lvl;
      if (r.level !== undefined && r.level !== null) lvl = r.level;
      else if (r.parentId === undefined || r.parentId === null) lvl = 0;
      else {
        const p = byId.get(r.parentId);
        lvl = p ? levelOf(p) + 1 : 0;
      }
      lvls.set(r.id, lvl);
      return lvl;
    };
    rows.forEach(levelOf);
    return { parentIds: parents, levels: lvls };
  }, [rows]);

  // 'fit' height mode (the default): size the scroll pane so its bottom edge
  // stays inside the viewport — the sticky totals footer is then always
  // visible ("locked") while rows scroll within the pane. Re-measured on
  // scroll/resize (rAF-throttled): as the card scrolls up the pane grows into
  // the freed space. 84px clears the floating filter dock (fixed, bottom
  // ~20px, ~44px tall); the 410px floor matches the old fixed default so
  // short viewports never regress.
  const scrollRef = useRef(null);
  const [fitHeight, setFitHeight] = useState(410);
  useEffect(() => {
    if (maxHeight !== 'fit') return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = scrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const next = Math.max(410, Math.round(window.innerHeight - top - 84));
      setFitHeight((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
    };
  }, [maxHeight]);

  // Default expand state recomputes as rows stream in (async loads) until
  // the user interacts, after which their state wins (uncontrolled mode).
  const defaultExpandedSet = useMemo(() => {
    if (Array.isArray(defaultExpanded)) return new Set(defaultExpanded);
    if (defaultExpanded === 'all') return new Set(parentIds);
    if (defaultExpanded === 'none') return new Set();
    // 'roots': expand top-level rows only
    const set = new Set();
    rows.forEach((r) => {
      if ((levels.get(r.id) ?? 0) === 0 && parentIds.has(r.id)) set.add(r.id);
    });
    return set;
  }, [defaultExpanded, rows, parentIds, levels]);

  const [internalExpanded, setInternalExpanded] = useState(null); // null = follow defaults
  const expandedSet = useMemo(() => {
    if (expanded !== undefined) return expanded instanceof Set ? expanded : new Set(expanded);
    return internalExpanded ?? defaultExpandedSet;
  }, [expanded, internalExpanded, defaultExpandedSet]);

  const applyExpanded = useCallback(
    (next) => {
      if (onExpandedChange) onExpandedChange([...next]);
      if (expanded === undefined) setInternalExpanded(next);
    },
    [expanded, onExpandedChange]
  );

  const toggleRow = useCallback(
    (id) => {
      const next = new Set(expandedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      applyExpanded(next);
    },
    [expandedSet, applyExpanded]
  );

  // Expand/collapse step ONE level at a time (kit-wide): expand opens every
  // parent on the shallowest level that still has a collapsed row; collapse
  // closes every parent on the deepest level that has an open row.
  const expandAll = useCallback(() => {
    const next = new Set(expandedSet);
    let target = null;
    parentIds.forEach((id) => {
      if (next.has(id)) return;
      const lvl = levels.get(id) ?? 0;
      if (target === null || lvl < target) target = lvl;
    });
    if (target === null) return;
    parentIds.forEach((id) => { if ((levels.get(id) ?? 0) <= target) next.add(id); });
    applyExpanded(next);
  }, [applyExpanded, parentIds, levels, expandedSet]);
  const collapseAll = useCallback(() => {
    const next = new Set(expandedSet);
    let target = null;
    next.forEach((id) => {
      const lvl = levels.get(id) ?? 0;
      if (target === null || lvl > target) target = lvl;
    });
    if (target === null) return;
    next.forEach((id) => { if ((levels.get(id) ?? 0) === target) next.delete(id); });
    applyExpanded(next);
  }, [applyExpanded, levels, expandedSet]);

  /* ---------- sorted rows (reorder siblings within each parent) ---------- */
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const childrenByParent = new Map();
    const roots = [];
    rows.forEach((r) => {
      const pid = r.parentId === undefined || r.parentId === null ? null : r.parentId;
      if (pid === null) roots.push(r);
      else {
        if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
        childrenByParent.get(pid).push(r);
      }
    });
    const valueOf = (r) => (sort.field === '__label' ? r.label : r.cells ? r.cells[sort.field] : undefined);
    const cmp = (a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      const aBlank = av === null || av === undefined || av === '';
      const bBlank = bv === null || bv === undefined || bv === '';
      if (aBlank && bBlank) return 0;
      if (aBlank) return 1; // blanks always last
      if (bBlank) return -1;
      let r;
      if (typeof av === 'number' && typeof bv === 'number') r = av - bv;
      else r = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sort.dir === 'asc' ? r : -r;
    };
    const out = [];
    const walk = (siblings) => {
      [...siblings].sort(cmp).forEach((r) => {
        out.push(r);
        const kids = childrenByParent.get(r.id);
        if (kids) walk(kids);
      });
    };
    walk(roots);
    return out;
  }, [rows, sort]);

  /* ---------- visible rows (depth-first order, post-sort) ---------- */
  const visibleRows = useMemo(() => {
    const visible = new Map(); // id -> bool
    const out = [];
    sortedRows.forEach((r) => {
      const hasParent = r.parentId !== undefined && r.parentId !== null;
      const parentVisible = hasParent ? visible.get(r.parentId) === true : true;
      const isVisible = !hasParent || (parentVisible && expandedSet.has(r.parentId));
      visible.set(r.id, isVisible);
      if (isVisible) out.push(r);
    });

    // Anchored detail rows (narrative commentary) glue to their anchor row
    // regardless of column sorting; they vanish when the anchor is hidden.
    if (out.some((r) => r.anchorId)) {
      const byAnchor = new Map();
      out.forEach((r) => {
        if (!r.anchorId) return;
        const list = byAnchor.get(r.anchorId) || [];
        list.push(r);
        byAnchor.set(r.anchorId, list);
      });
      const glued = [];
      out.forEach((r) => {
        if (r.anchorId) return;
        glued.push(r);
        const list = byAnchor.get(r.id);
        if (list) glued.push(...list);
      });
      return glued;
    }
    return out;
  }, [sortedRows, expandedSet]);

  /* ---------- column groups ---------- */
  const hasGroups = columns.some((c) => c.group !== undefined && c.group !== null);
  const groupsOf = (cols) => {
    const out = [];
    cols.forEach((c) => {
      const last = out[out.length - 1];
      if (last && last.name === (c.group ?? '')) last.count += 1;
      else out.push({ name: c.group ?? '', count: 1 });
    });
    return out;
  };
  const groups = useMemo(() => (hasGroups ? groupsOf(columns) : []), [columns, hasGroups]);
  const leftGroups = useMemo(() => (hasGroups && centered ? groupsOf(leftColumns) : []), [leftColumns, hasGroups, centered]);
  const rightGroups = useMemo(() => (hasGroups ? (centered ? groupsOf(rightColumns) : groupsOf(columns)) : []), [rightColumns, columns, hasGroups, centered]);

  // first column index of each group → hairline separator
  const gapIndexes = useMemo(() => {
    const set = new Set();
    if (!hasGroups) return set;
    let idx = 0;
    groups.forEach((g) => {
      set.add(idx);
      idx += g.count;
    });
    return set;
  }, [groups, hasGroups]);

  const colClass = (col, i) => {
    const cls = [];
    if (gapIndexes.has(i)) cls.push('rn-gap');
    if (col.align === 'left') cls.push('rn-align-left');
    if (col.align === 'center') cls.push('rn-align-center');
    if (col.variant === 'budget') cls.push('rn-budget');
    if (col.variant === 'total' || (totalGroup && col.group === totalGroup)) cls.push('rn-totalcol');
    return cls.join(' ');
  };

  const renderGroupHeaders = (list) =>
    list.map((g, i) => (
      <th
        key={`${g.name}-${i}`}
        colSpan={g.count}
        className={`rn-gap${totalGroup && g.name === totalGroup ? ' rn-total-h' : ''}`}
      >
        {g.name}
      </th>
    ));

  const sortArrow = (field) =>
    sort?.field === field ? <span className="rn-sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span> : null;

  const renderCell = (col, row) => {
    const value = row.cells ? row.cells[col.key] : undefined;
    if (col.render) return col.render(value, row, col);
    if (value === undefined) return null; // blank cell (e.g. group header spacer rows)
    // row.format overrides the column format for rows whose unit differs from
    // the column's (e.g. the P&L Statistics "Occupancy %" row inside numeric
    // Actual/Budget/Var columns).
    if (col.variant === 'variance') {
      return <VarianceChip value={value} format={row.format || col.format || 'number'} currency={currency} invert={col.invertVariance} />;
    }
    return formatValue(value, row.format || col.format || 'number', currencyOpts);
  };

  const renderFooterCell = (col) => {
    const value = footer?.cells ? footer.cells[col.key] : undefined;
    if (col.renderFooter) return col.renderFooter(value, footer, col);
    // Columns that only define `render` (e.g. ratio→percent formatters) must
    // format the footer the same way, or raw ratios show as "0".
    if (col.render) return col.render(value, footer, col);
    if (value === undefined) return null;
    if (col.variant === 'variance') {
      return <VarianceChip value={value} format={col.format || 'number'} currency={currency} invert={col.invertVariance} />;
    }
    return formatValue(value, col.format || 'number', currencyOpts);
  };

  const rowClass = (row) => {
    if (selectedRowId && row.id === selectedRowId) {
      // appended below with the variant classes
    }
    const lvl = Math.min(levels.get(row.id) ?? 0, 4);
    const cls = [`rn-mx-l${lvl}`];
    // Banding: a row WITH children is an aggregating subtotal (tinted band);
    // a leaf is detail (plain). Lets nested totals/subtotals/detail read at a
    // glance independent of which page supplies the hierarchy.
    cls.push(parentIds.has(row.id) ? 'rn-mx-parent' : 'rn-mx-leaf');
    if (row.variant === 'subtotal') cls.push('rn-mx-subtotal');
    if (row.variant === 'emphasis' || row.variant === 'net') cls.push('rn-mx-emphasis');
    if (row.severity) cls.push(`rn-mx-sev-${row.severity}`);
    return cls.join(' ');
  };

  const { isFullscreen, toggle: toggleFullscreen, exit: exitFullscreen } = useFullscreenCard();
  const hasExportMenu = Array.isArray(downloadOptions) && downloadOptions.length > 0;
  const showToolbar = title || pill || tools || expandControls || fullscreenControl || onDownloadCsv || hasExportMenu || columnPrefs;

  return (
    <>
      <FullscreenBackdrop active={isFullscreen} onClose={exitFullscreen} />
      <section className={`rn-card${isFullscreen ? ' rn-card-fs' : ''}${className ? ` ${className}` : ''}`}>
      {showToolbar && (
        <div className="rn-table-toolbar">
          {title && <h2>{title}</h2>}
          {pill && <span className="rn-pill-note">{pill}</span>}
          <div className="rn-spacer" />
          {tools}
          {columnPrefs && <ColumnPickerControl columnPrefs={columnPrefs} sections={columnPickerSections} />}
          <div className="rn-tools">
            {expandControls && parentIds.size > 0 && (
              <>
                <button type="button" className="rn-icon-btn" title="Expand one level" onClick={expandAll}>
                  <Maximize2 />
                </button>
                <button type="button" className="rn-icon-btn" title="Collapse one level" onClick={collapseAll}>
                  <Minimize2 />
                </button>
              </>
            )}
            {hasExportMenu ? (
              <ExportMenuControl options={downloadOptions} />
            ) : onDownloadCsv ? (
              <button type="button" className="rn-icon-btn" title="Download CSV" onClick={onDownloadCsv}>
                <Download />
              </button>
            ) : null}
            {fullscreenControl && <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />}
          </div>
        </div>
      )}

      {/* rn-scrollwrap anchors the mobile right-edge "more columns" fade */}
      <div className="rn-scrollwrap">
      <div
        ref={scrollRef}
        className="rn-matrix-scroll"
        style={{ maxHeight: isFullscreen ? undefined : (maxHeight === 'fit' ? fitHeight : maxHeight) }}
        onScroll={
          onEndReached
            ? (e) => {
                const el = e.currentTarget;
                // endless scroll: fire when within ~2 rows of the bottom;
                // the caller guards against duplicate fetches
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) onEndReached();
              }
            : undefined
        }
      >
        <table className={`rn-matrix${centered ? ' rn-mx-centered' : ''}`} ref={matrixRef}>
          <thead>
            {hasGroups && (
              <tr className="rn-mx-groups">
                {renderGroupHeaders(leftGroups)}
                <th
                  className="rn-rowhead rn-sortable"
                  rowSpan={2}
                  style={{ minWidth: 230 }}
                  onClick={() => toggleSort('__label')}
                  aria-sort={sort?.field === '__label' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {rowHeaderLabel}
                  {sortArrow('__label')}
                </th>
                {renderGroupHeaders(rightGroups)}
              </tr>
            )}
            <tr className="rn-mx-cols">
              {!hasGroups && (
                <th
                  className="rn-rowhead rn-sortable"
                  style={{ minWidth: 230 }}
                  onClick={() => toggleSort('__label')}
                  aria-sort={sort?.field === '__label' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {rowHeaderLabel}
                  {sortArrow('__label')}
                </th>
              )}
              {columns.map((col, i) => {
                const sortable = col.sortable !== false;
                return (
                  <th
                    key={col.key}
                    className={`${colClass(col, i)}${sortable ? ' rn-sortable' : ''}`}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={sortable ? () => toggleSort(col.key) : undefined}
                    aria-sort={sort?.field === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.label}
                    {sortable && sortArrow(col.key)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className="rn-sk-classic-only">
                <td colSpan={columns.length + 1} style={{ padding: 0, border: 'none' }}>
                  <div className="lodge-skeleton rn-skeleton-block" />
                </td>
              </tr>
            )}
            {loading && <SkeletonTableRows columns={columns.length + 1} rows={6} />}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} style={{ border: 'none' }}>
                  <div className="rn-empty">{emptyText}</div>
                </td>
              </tr>
            )}
            {!loading &&
              visibleRows.map((row) => {
                if (row.fullRender) {
                  return (
                    <tr key={row.id} className={`rn-mx-detailrow${row.severity ? ` rn-mx-sev-${row.severity}` : ''}`}>
                      <td colSpan={columns.length + 1}>{row.fullRender}</td>
                    </tr>
                  );
                }
                const expandable = parentIds.has(row.id);
                const isOpen = expandedSet.has(row.id);
                return (
                  <tr
                    key={row.id}
                    className={`${rowClass(row)}${selectedRowId && row.id === selectedRowId ? ' rn-row-selected' : ''}${onRowClick ? ' rn-row-clickable' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {leftColumns.map((col, i) => (
                      <td key={col.key} className={colClass(col, i)}>
                        {renderCell(col, row)}
                      </td>
                    ))}
                    <td className="rn-rowlabel">
                      {expandable && onRowClick ? (
                        /* cross-filterable matrix: the arrow alone drills down
                           (stopPropagation); clicking anywhere else on the row
                           applies the cross-filter */
                        <span className="rn-rowlabel-btn">
                          <button
                            type="button"
                            className="rn-twisty-btn"
                            title={isOpen ? 'Collapse' : 'Drill down'}
                            aria-expanded={isOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(row.id);
                            }}
                          >
                            <span className={`rn-twisty${isOpen ? ' rn-twisty-open' : ''}`}>▶</span>
                          </button>
                          {row.label}
                        </span>
                      ) : expandable ? (
                        <button
                          type="button"
                          className="rn-rowlabel-btn"
                          onClick={() => toggleRow(row.id)}
                          aria-expanded={isOpen}
                        >
                          <span className={`rn-twisty${isOpen ? ' rn-twisty-open' : ''}`}>▶</span>
                          {row.label}
                        </button>
                      ) : (
                        <>
                          <span className="rn-twisty-spacer" />
                          {row.label}
                        </>
                      )}
                      {renderRowHeaderExtra && renderRowHeaderExtra(row)}
                    </td>
                    {(centered ? rightColumns : columns).map((col, j) => {
                      const i = centered ? leftColumns.length + j : j;
                      return (
                        <td key={col.key} className={colClass(col, i)}>
                          {renderCell(col, row)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
          {footer && (
            <tfoot>
              <tr>
                {leftColumns.map((col, i) => (
                  <td key={col.key} className={colClass(col, i)}>
                    {renderFooterCell(col)}
                  </td>
                ))}
                <td className="rn-rowlabel">{footer.label ?? 'Total'}</td>
                {(centered ? rightColumns : columns).map((col, j) => {
                  const i = centered ? leftColumns.length + j : j;
                  return (
                    <td key={col.key} className={colClass(col, i)}>
                      {renderFooterCell(col)}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </div>

      {legend && <CardLegend items={legend} />}
      </section>
    </>
  );
}

export default ReportMatrix;

/**
 * Helper for the pages: builds a CSV string from matrix columns/rows
 * (all rows, not just visible). Useful for onDownloadCsv handlers.
 */
export function matrixToCsv({ columns, rows, footer, rowHeaderLabel = '' }) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // Narrative commentary rows are presentation-only — the PDF export carries
  // the narratives; CSV stays numbers.
  rows = (rows || []).filter((r) => !r.fullRender);
  const header = [rowHeaderLabel, ...columns.map((c) => (c.group ? `${c.group} ${c.label}` : c.label))];
  const lines = [header.map(esc).join(',')];
  rows.forEach((r) => {
    lines.push([r.label, ...columns.map((c) => r.cells?.[c.key] ?? '')].map(esc).join(','));
  });
  if (footer) {
    lines.push([footer.label ?? 'Total', ...columns.map((c) => footer.cells?.[c.key] ?? '')].map(esc).join(','));
  }
  return lines.join('\n');
}
